import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './sectionsButtonSelector.module.scss';
import clsx from 'clsx';
import { Check, ChevronDown, X } from 'lucide-react';

const SectionAccordion = ({ section, selectedList, setSelectedList }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedSection = selectedList?.find((selected) => selected.sezione == section.id) || {};
  const selectedFields = selectedSection.fields || [];
  const hasSelection = selectedFields.length > 0;

  const isOptionSelected = (field, value) =>
    Number(selectedSection[field]) === Number(value);

  const excludedFields = ['sezione', 'disabilitato', 'utenteBusiness', 'idPermesso'];

  useEffect(() => {
    if (!selectedSection.sezione || selectedFields.length > 0) return;

    const sectionIndex = selectedList?.findIndex((selected) => selected.sezione == section.id);
    if (sectionIndex < 0) return;

    const rebuiltFields = Object.keys(selectedSection)
      .filter((key) => !excludedFields.includes(key))
      .filter((key) =>
        section.options.some(
          (option) => option.field === key && option.value === selectedSection[key],
        ),
      );

    if (rebuiltFields.length === 0) return;

    setSelectedList(
      selectedList.map((item, index) =>
        index === sectionIndex ? { ...item, fields: rebuiltFields } : item,
      ),
    );
  }, []);

  const handleSelect = (option) => {
    const sectionIndex = selectedList?.findIndex((selected) => selected.sezione == section.id);

    if (sectionIndex > -1) {
      const updatedSection = { ...selectedList[sectionIndex] };
      const fields = [...(updatedSection.fields || [])];
      const isSameSelection = updatedSection[option.field] === option.value;

      if (isSameSelection) {
        delete updatedSection[option.field];
        const fieldIndex = fields.indexOf(option.field);
        if (fieldIndex > -1) fields.splice(fieldIndex, 1);
      } else {
        updatedSection[option.field] = option.value;
        if (!fields.includes(option.field)) {
          fields.push(option.field);
        }
      }

      updatedSection.fields = fields;

      if (fields.length < 1) {
        setSelectedList(selectedList.filter((_, index) => index !== sectionIndex));
      } else {
        setSelectedList(
          selectedList.map((item, index) => (index === sectionIndex ? updatedSection : item)),
        );
      }
    } else {
      setSelectedList([
        ...selectedList,
        {
          sezione: section.id,
          [option.field]: option.value,
          fields: [option.field],
        },
      ]);
    }
  };

  const deleteSection = () => {
    setSelectedList(selectedList.filter((selected) => selected.sezione != section.id));
  };

  return (
    <details
      className={clsx(styles.section_accordion, {
        [styles.section_accordion_open]: isOpen,
        [styles.section_accordion_selected]: hasSelection && !isOpen,
      })}
      onToggle={(e) => setIsOpen(e.currentTarget.open)}
    >
      <summary className={styles.summary}>
        <div className={styles.summary_main}>
          <span className={styles.section_title}>{section.title}</span>
          {hasSelection ? (
            <span className={styles.count_badge}>+{selectedFields.length}</span>
          ) : null}
        </div>
        <div className={styles.summary_actions}>
          {hasSelection && !isOpen ? (
            <button
              type="button"
              className={styles.clear_btn}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                deleteSection();
              }}
              aria-label="clear"
            >
              <X size={14} strokeWidth={2.4} />
            </button>
          ) : null}
          <span className={styles.chevron} aria-hidden>
            <ChevronDown size={18} strokeWidth={2.4} />
          </span>
        </div>
      </summary>

      <div className={styles.options_box}>
        {Array.isArray(section.options) &&
          section.options.map((option) => {
            const { name, field, value } = option;
            const checked = isOptionSelected(field, value);
            return (
              <button
                key={`${section.id}-${field}-${value}`}
                type="button"
                className={clsx(styles.option_item, {
                  [styles.option_item_checked]: checked,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSelect({ field, value });
                }}
              >
                <span className={styles.option_value}>{name}</span>
                <span
                  className={clsx(styles.option_indicator, {
                    [styles.option_indicator_checked]: checked,
                  })}
                  aria-hidden
                >
                  {checked ? <Check size={12} strokeWidth={3} /> : null}
                </span>
              </button>
            );
          })}
      </div>
    </details>
  );
};

const SectionsButtonSelector = ({
  title: titleProp,
  sections,
  selectedList = [],
  setSelectedList,
  error,
}) => {
  const { t } = useTranslation();
  const title = titleProp || t('settings.sectionsButtonSelector.title');

  return (
    <div className={styles.sections_wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {error ? <div className={styles.error_text}>{error}</div> : null}
      </div>
      <div className={styles.sections}>
        {sections.map((section) => (
          <SectionAccordion
            key={section.id}
            section={section}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
          />
        ))}
      </div>
    </div>
  );
};

export default SectionsButtonSelector;
